/**
 * The words a reference document's row shows when its read failed.
 *
 * The server saves why a read failed as a kind, and this module words it.
 * The raw error the server also keeps is never shown: it can be a stack
 * trace or provider text that means nothing to the reader. A failure saved
 * before kinds existed has none, and gets a general line.
 *
 * Pure, so the wording is pinned without rendering the list.
 */
import type { ExtractionFailureKind, ReferenceDoc } from '@/types'

type ProgressDoc = Pick<ReferenceDoc, 'readProgress' | 'effectiveKind'>

/** What a document's parts are called: defects in a Defects document,
 * requirements otherwise. */
function partNoun(doc: Pick<ReferenceDoc, 'effectiveKind'>): string {
  return doc.effectiveKind === 'defects' ? 'defects' : 'requirements'
}

/** "40 of 120 requirements" for a read that takes more than one round;
 * null for a read that fits one round, which needs no count. */
export function readProgressCount(doc: ProgressDoc): string | null {
  const p = doc.readProgress
  if (!p || p.partIds.length <= p.roundSize) return null
  return `${p.partsDone} of ${p.partIds.length} ${partNoun(doc)}`
}

/** The pill while a document is being read: "Finding requirements…"
 * while its parts are found, "Reading · 40 of 120 requirements" through a
 * read in rounds, null for a one-round read (the plain "Extracting…"). */
export function readingLabel(doc: ProgressDoc): string | null {
  const p = doc.readProgress
  if (!p) return null
  if (p.partIds.length === 0) return `Finding ${partNoun(doc)}…`
  const count = readProgressCount(doc)
  return count ? `Reading · ${count}` : null
}

/** Whether a stopped read has its parts fixed, so retrying it should
 * carry on (keeping its progress and a halved round size) rather than
 * start over. True even before any part is done. */
export function canCarryOn(doc: Pick<ReferenceDoc, 'readProgress'>): boolean {
  return (doc.readProgress?.partIds.length ?? 0) > 0
}

/** How many parts a stopped read already has tests for, or 0. */
export function partsAlreadyRead(doc: ProgressDoc): number {
  return doc.readProgress?.partsDone ?? 0
}

const KIND_TEXT: Record<ExtractionFailureKind, string> = {
  'no-response': 'No response from model',
  busy: 'Model provider busy',
  connection: 'Connection dropped',
  'out-of-credits': 'Out of AI credits',
  'parts-not-matched': "The model didn't match its tests to this document's requirements",
  'text-changed': 'The document changed while it was being read',
  other: 'Reading this document failed',
}

const GENERAL = 'Reading this document failed'

/** The reason, in words, for a kind; the general line for none. */
function reasonText(kind: ExtractionFailureKind | undefined): string {
  return (kind && KIND_TEXT[kind]) || GENERAL
}

/** The line under a retrying document: "No response from model,
 * retrying in 2 mins". Minutes round up and never show below 1; once
 * the time has passed it says the retry is starting. */
export function retryingLine(
  doc: Pick<ReferenceDoc, 'extractionFailureKind' | 'extractionRetryAt'> & Partial<ProgressDoc>,
  now: number = Date.now(),
): string {
  const reason = reasonText(doc.extractionFailureKind)
  const at = doc.extractionRetryAt ? new Date(doc.extractionRetryAt).getTime() : NaN
  const when = Number.isNaN(at) || at <= now
    ? 'retrying now'
    : (() => {
        const mins = Math.max(1, Math.ceil((at - now) / 60_000))
        return `retrying in ${mins} ${mins === 1 ? 'min' : 'mins'}`
      })()
  const done = partsAlreadyRead(doc)
  return done > 0
    ? `${reason}, ${when}. The ${done} ${partNoun(doc)} already read are kept.`
    : `${reason}, ${when}`
}

/** The line under a failed document: "No response from model after 2
 * tries." A failure that ended on its first try, or one saved before
 * kinds existed, leaves the count out. */
export function failedLine(
  doc: Pick<ReferenceDoc, 'extractionFailureKind' | 'extractionTries'> & Partial<ProgressDoc>,
): string {
  const reason = reasonText(doc.extractionFailureKind)
  const tries = doc.extractionTries
  const why = tries && tries > 1 ? `${reason} after ${tries} tries.` : `${reason}.`
  const done = partsAlreadyRead(doc)
  const total = doc.readProgress?.partIds.length ?? 0
  if (done === 0 || total === 0) return why
  const noun = partNoun(doc)
  return `Stopped after ${done} of ${total} ${noun}: ${why.charAt(0).toLowerCase()}${why.slice(1)} `
    + `The ${done} already read are kept.`
}
